// Considered: ArcFilterMolecule (the standalone lane) — rejected as a base for
// this extraction because it owns list chrome (header/collapse/add-row/remove)
// that a single-arc value editor (hosted inside FilterEditorPanelMolecule) must
// not duplicate. This IS the lane's row body, lifted out (`expandedIndex` moves
// from ArcFilterMolecule's list-level state to this component's own local
// `expanded` state — one editor instance, one arc). ArcFilterMolecule keeps
// consuming this component for each of its rows. See spec
// context-selector-editor.md Phase 2 (SC2) Reuse Audit.
import React, { useCallback, useId, useMemo, useState } from 'react';
import { useTranslation } from 'localization';
import type { ArcFilter, ArcHealthStatus, ArcLifecycle } from 'types/filters';
import DropdownAtom from '../../atoms/Dropdown/DropdownAtom';
import EmptyStateAtom from '../../atoms/EmptyState/EmptyStateAtom';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import { SegmentedControlAtom } from '../../atoms/SegmentedControl';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import { buildFilterArcOptions } from '../../../utils/filterPropertyBuilder';
import { getArcHealthModifier } from '../../../utils/arc/healthUtils';
import type { ArcTemplateOption } from '../../../hooks/useArcTemplates';
import './ArcValueEditorMolecule.scss';

/** The parity blocks that render the arc value editor's lines. */
export type ArcParityBlock = 'uf-arc' | 'uf-row' | 'uf-column';

export interface ArcValueEditorMoleculeProps {
  /** Published arc templates the author can see — feeds the fused selector + the stage picker. */
  arcTemplates: ArcTemplateOption[];
  /** The single `ArcFilter` this editor instance edits (one row = one arc). */
  value: ArcFilter;
  onChange: (next: ArcFilter) => void;
  /** Omit to hide the remove affordance — the host (e.g. FilterEditorPanel) may provide its own. */
  onRemove?: () => void;
  readOnly?: boolean;
  /** DOM index for `data-testid` parity with list hosts (e.g. `ArcFilterMolecule`). Default 0. */
  index?: number;
  /**
   * Controls the "Edit arc condition" disclosure — the at-least stepper this editor
   * otherwise keeps folded away behind it. A host with the room to show the stepper at
   * rest (a full-width lane board, an artboard documenting the open reading) passes
   * `expanded` and drives it from `onExpandedChange`. Omitted (the default), the
   * disclosure stays uncontrolled and starts closed, exactly as today.
   */
  expanded?: boolean;
  /**
   * Fires whenever the disclosure is toggled, with the state it is moving to. Useful
   * uncontrolled too (a host that mirrors the reading elsewhere). Omitted by default.
   */
  onExpandedChange?: (expanded: boolean) => void;
  className?: string;
  /**
   * Parity instrumentation only (EAINT-908 Phase 3). Declares which contract block this
   * instance stands in for. `'uf-arc'` (default) keeps today's fixed `uf-arc.*` ids —
   * this component's own C3 lane-config block. `'uf-row'` renders the SAME
   * `context/arc-filter.jsx` lines a second (and third) time inside B1's row-state
   * gallery, where every occurrence needs its own numbered id — supply every override
   * prop below explicitly; with `parityBlock='uf-row'`/`'uf-column'` and no override,
   * the datum goes untagged rather than guessing a colliding number. `'uf-column'`
   * renders the same lines a fourth time inside D3's column adapter (`ui-column`).
   */
  parityBlock?: ArcParityBlock;
  /** Parity instrumentation only — the fused selector's own trigger button (arc-filter.jsx:254, `uf-arc.button` by default). */
  triggerButtonDatumId?: string;
  /** Parity instrumentation only — the trigger's leading glyph (arc-filter.jsx:258, `uf-arc.glyph#2` by default). */
  triggerGlyphDatumId?: string;
  /** Parity instrumentation only — the open selector menu's per-option row (arc-filter.jsx:225, `uf-arc.button#2` by default). */
  menuButtonDatumId?: string;
  /** Parity instrumentation only — the menu option's glyph (arc-filter.jsx:230, `uf-arc.glyph#3` by default). */
  menuGlyphDatumId?: string;
  /** Parity instrumentation only — the is/is-not tab strip (arc-filter.jsx:268, `uf-arc.tabs` by default). */
  tabsDatumId?: string;
  /** Parity instrumentation only — the Health axis label (arc-filter.jsx:296, `uf-arc.health` by default). */
  healthLabelDatumId?: string;
  /** Parity instrumentation only — each Health option chip (arc-filter.jsx:298, `uf-arc.chip#2` by default). */
  healthChipDatumId?: string;
  /** Parity instrumentation only — the Stage axis label (arc-filter.jsx:303, `uf-arc.stage` by default). */
  stageLabelDatumId?: string;
  /** Parity instrumentation only — each Stage option chip, only rendered once a template narrows the stage list (arc-filter.jsx:309, `uf-arc.chip#3` by default). */
  stageChipDatumId?: string;
  /** Parity instrumentation only — the Lifecycle axis label (arc-filter.jsx:319, `uf-arc.lifecycle` by default). */
  lifecycleLabelDatumId?: string;
  /** Parity instrumentation only — each Lifecycle option chip (arc-filter.jsx:321, `uf-arc.chip#4` by default). */
  lifecycleChipDatumId?: string;
  /** Parity instrumentation only — the "At least" min-count label (arc-filter.jsx:328, `uf-arc.at-least` by default). */
  atLeastLabelDatumId?: string;
  /** Parity instrumentation only — the minCount stepper's decrease button (arc-filter.jsx:330, `uf-arc.button#5` by default). */
  minCountDecreaseDatumId?: string;
  /** Parity instrumentation only — the minCount stepper's increase button (arc-filter.jsx:332, `uf-arc.button#6` by default). */
  minCountIncreaseDatumId?: string;
  /**
   * Renders the fused arc-selector as a static label instead of the
   * interactive dropdown — the arc/template choice is fixed by the host (a
   * `focusArc` column already committed to one arc lane), so re-picking it
   * from inside the popover would contradict the column it was opened from
   * (mirrors `PerspectiveValueEditorMolecule`'s `lockPerspective`). Every
   * existing caller omits this.
   */
  lockArcSelector?: boolean;
  /**
   * Renders only the named axes' label + chips; omit to render every axis
   * (Health, Stage, Lifecycle) as today. A `focusArc` column scoped to a
   * single axis (e.g. Atlas's Stage column) narrows to `['stage']` so the
   * editor doesn't offer predicates the column has no way to display.
   */
  axes?: Array<'health' | 'stage' | 'lifecycle'>;
  /**
   * The host's `useArcTemplates()` fetch is still in flight — the selector shows
   * a loading indicator instead of pretending the "any"/"primary" pair (no
   * templates in `arcTemplates` yet) is the whole catalog.
   */
  templatesLoading?: boolean;
  /**
   * The host's `useArcTemplates()` fetch failed. The selector still works for
   * "any"/"primary" arc (the hook's own degrade-gracefully contract — a failed
   * template list must not block every other axis), but a note explains why
   * no by-template options are offered.
   */
  templatesFailed?: boolean;
}

/** The fused selector collapses "which arcs" into one control (brief §6). */
const TEMPLATE_VALUE_PREFIX = 'tpl:';

// The container each occurrence renders inside — `uf-row`/`uf-types` share `ui-row`,
// `uf-column` renders inside D3's column adapter (`ui-column`); the block's own
// C3 lane-config artboard (`uf-arc`, the default) is `ui-lane-config`.
const CONTAINER_BY_BLOCK: Record<ArcParityBlock, string> = {
  'uf-arc': 'ui-lane-config',
  'uf-row': 'ui-row',
  'uf-column': 'ui-column',
};
// What the selector menu's portal loses: the panel mounts on `document.body`, so the
// collection the host declares around this row — the arc board's `each(rows)`, the
// column popover's `each(viewFilters)` — is not an ancestor of the option rows and is
// re-declared on them (PAR-5 holds the option to the prototype's full container path).
const PORTAL_CONTAINER_BY_BLOCK: Record<ArcParityBlock, string> = {
  'uf-arc': 'ui-lane-config > each(rows)',
  'uf-row': 'ui-row',
  'uf-column': 'ui-column > each(viewFilters)',
};

/** Fused selector value → the `arcSelector`/`templateSystemName` pair it means. */
function parseSelectorValue(v: string): Pick<ArcFilter, 'arcSelector' | 'templateSystemName'> {
  if (v === 'primary') return { arcSelector: 'primary' };
  if (v.startsWith(TEMPLATE_VALUE_PREFIX)) {
    // A template pick narrows the candidate arcs to that template; it does not
    // assert primacy, so the selector stays 'any' (brief §3).
    return { arcSelector: 'any', templateSystemName: v.slice(TEMPLATE_VALUE_PREFIX.length) };
  }
  return { arcSelector: 'any' };
}

function toSelectorValue(filter: ArcFilter): string {
  if (filter.templateSystemName) return `${TEMPLATE_VALUE_PREFIX}${filter.templateSystemName}`;
  return filter.arcSelector === 'primary' ? 'primary' : 'any';
}

const ArcValueEditorMolecule: React.FC<ArcValueEditorMoleculeProps> = ({
  arcTemplates,
  value: filter,
  onChange,
  onRemove,
  readOnly = false,
  index = 0,
  expanded: controlledExpanded,
  onExpandedChange,
  className = '',
  parityBlock,
  triggerButtonDatumId,
  triggerGlyphDatumId,
  menuButtonDatumId,
  menuGlyphDatumId,
  tabsDatumId,
  healthLabelDatumId,
  healthChipDatumId,
  stageLabelDatumId,
  stageChipDatumId,
  lifecycleLabelDatumId,
  lifecycleChipDatumId,
  atLeastLabelDatumId,
  minCountDecreaseDatumId,
  minCountIncreaseDatumId,
  lockArcSelector = false,
  axes,
  templatesLoading = false,
  templatesFailed = false,
}) => {
  const { t } = useTranslation();
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(false);
  const expanded = controlledExpanded ?? uncontrolledExpanded;
  const toggleExpanded = useCallback(() => {
    const next = !expanded;
    // A controlled host owns the value; the internal one is still kept in step so
    // dropping the prop later resumes from what the reader last saw.
    setUncontrolledExpanded(next);
    onExpandedChange?.(next);
  }, [expanded, onExpandedChange]);
  // The min-count region the "Edit arc condition" disclosure reveals (one of two
  // mutually exclusive renders, so both carry the same id).
  const advancedRegionId = useId();
  // The narrow-by-template picker's label target (EAINT-936 OQ-11).
  const narrowTemplateSelectId = useId();

  // Parity instrumentation only (EAINT-908 Phase 3) — see `parityBlock` doc comment.
  // `'uf-arc'` (default) keeps today's fixed ids; `'uf-row'`/`'uf-column'` require an
  // explicit override per occurrence rather than guessing a numbered suffix.
  const dtm = useCallback(
    (override: string | undefined, ufArcDefault: string): string | undefined =>
      override ?? (parityBlock === 'uf-row' || parityBlock === 'uf-column' ? undefined : ufArcDefault),
    [parityBlock]
  );

  const container = CONTAINER_BY_BLOCK[parityBlock ?? 'uf-arc'];

  const templateByName = useMemo(
    () => new Map(arcTemplates.map((tpl) => [tpl.systemName, tpl])),
    [arcTemplates]
  );

  const selectorOptions = useMemo(
    () => [
      // Glyphs mirror the prototype's fused selector (arc-filter.jsx AC_SELECTORS):
      // "any" gets infinity, "primary" gets star, every template falls back to
      // git-branch — the prototype's own AC_SEL_META fallback, since a real
      // `ArcTemplateOption` carries no per-template icon.
      {
        label: t('context.editor.arcFilters.selector.any'),
        value: 'any',
        glyph: 'lucide:infinity',
      },
      {
        label: t('context.editor.arcFilters.selector.primary'),
        value: 'primary',
        glyph: 'lucide:star',
      },
      ...arcTemplates.map((tpl) => ({
        label: t('context.editor.arcFilters.selector.template', {
          name: tpl.name,
        }),
        value: `${TEMPLATE_VALUE_PREFIX}${tpl.systemName}`,
        glyph: 'lucide:git-branch',
      })),
    ],
    [arcTemplates, t]
  );

  // uf-arc.button#2 (EAINT-908 Phase 3) — the open selector menu's per-option
  // row (arc-filter.jsx:225-230, ACSelectorMenu) is PrimeReact's own
  // `<li role="option">`, so the row datum is stamped onto that element through
  // the Dropdown's pass-through (`pt.item`) rather than onto a div inside it
  // that would need a duplicate `option` role to count as actionable. The panel
  // portals to `document.body`, outside this component's own `ui-lane-config`
  // subtree, so the container is re-declared there too.
  const selectorOptionPassThrough = useMemo(
    () => ({
      item: () => ({
        'data-datum': dtm(menuButtonDatumId, 'uf-arc.button#2'),
        'data-kind': 'button',
        // …and the option is one of the menu's `each(AC_SELECTORS)` collection.
        'data-container': `${PORTAL_CONTAINER_BY_BLOCK[parityBlock ?? 'uf-arc']} > each(AC_SELECTORS)`,
      }),
    }),
    [dtm, menuButtonDatumId, parityBlock]
  );
  // uf-arc.button — the selector control (ACSelectorControl), which the prototype renders
  // as one button wrapping the glyph and label. PrimeReact renders it as a combobox: its
  // keyboard control is the focus `<input>` ahead of the label, so the datum goes there
  // (the chevron comes after the label; a role on the ROOT makes a button wrapping the
  // focus input, axe `nested-interactive`).
  const selectorFocusInputAttrs = useMemo(
    () => ({ 'data-datum': dtm(triggerButtonDatumId, 'uf-arc.button'), 'data-kind': 'button' }),
    [dtm, triggerButtonDatumId]
  );

  // uf-arc.glyph#3 — the option row's glyph, rendered inside the li above.
  const selectorItemTemplate = useCallback(
    (option: { label: string; value: string; glyph: string }) => (
      <>
        {/* uf-arc.eyebrow (EAINT-908 Phase 4) — the selector menu's "By template" section
            header (arc-filter.jsx:224, `divider && ...`), rendered once above the first
            template option (index 2 of AC_SELECTORS: any, primary, then templates). */}
        {arcTemplates.length > 0 && option.value === selectorOptions[2]?.value && (
          <div className="arc-value-editor-molecule__selector-group-label" data-kind="label">
            {t('context.editor.arcFilters.byTemplate')}
          </div>
        )}
        <div className="arc-value-editor-molecule__selector-option" data-container="each(AC_SELECTORS)">
          <span
            className="arc-value-editor-molecule__selector-option-glyph"
            data-datum={dtm(menuGlyphDatumId, 'uf-arc.glyph#3')}
            data-kind="glyph"
          >
            <GlyphAtom glyph={option.glyph} />
          </span>
          <span className="arc-value-editor-molecule__selector-option-label">{option.label}</span>
        </div>
      </>
    ),
    [dtm, menuGlyphDatumId, arcTemplates, selectorOptions, t]
  );

  // uf-arc.button / uf-arc.glyph#2 (EAINT-908 Phase 3) — the fused selector's OWN
  // trigger (arc-filter.jsx:254/258, `ACSelectorControl`'s closed-state button).
  // `valueTemplate` replaces only the label content; PrimeReact spreads unknown
  // props (like the `data-testid` already on the control below) onto the
  // trigger's own root element, so the button-level datum is declared there.
  const selectorValueTemplate = useCallback(
    (option: { label: string; value: string; glyph: string } | null) => {
      const resolved = option ?? selectorOptions.find((o) => o.value === toSelectorValue(filter));
      if (!resolved) return null;
      return (
        <span className="arc-value-editor-molecule__selector-value">
          <span
            className="arc-value-editor-molecule__selector-value-glyph"
            data-datum={dtm(triggerGlyphDatumId, 'uf-arc.glyph#2')}
            data-kind="glyph"
          >
            <GlyphAtom glyph={resolved.glyph} />
          </span>
          <span>{resolved.label}</span>
        </span>
      );
    },
    [filter, selectorOptions, dtm, triggerGlyphDatumId]
  );

  const changeSelector = useCallback(
    (selectorValue: string) => {
      const next: ArcFilter = { ...filter, ...parseSelectorValue(selectorValue) };
      if (!parseSelectorValue(selectorValue).templateSystemName) delete next.templateSystemName;
      // Stage ids are template-and-version specific, so a stage selection cannot
      // survive a template change — carrying it over would author a predicate
      // that silently matches nothing.
      if (next.templateSystemName !== filter.templateSystemName) delete next.stageId;
      onChange(next);
    },
    [filter, onChange]
  );

  /** Drops an axis entirely when cleared — an omitted axis is unconstrained (brief §3). */
  const updateAxis = useCallback(
    (axis: 'stageId' | 'healthStatus' | 'lifecycle', next: string[]) => {
      const row: ArcFilter = { ...filter };
      if (next.length === 0) delete row[axis];
      else if (axis === 'healthStatus') row.healthStatus = next as ArcHealthStatus[];
      else if (axis === 'lifecycle') row.lifecycle = next as ArcLifecycle[];
      else row.stageId = next;
      onChange(row);
    },
    [filter, onChange]
  );

  /** Adds/removes `value` from a predicate axis array — a toggle, not a replace. */
  const toggleInAxis = useCallback((list: string[] | undefined, value: string): string[] => {
    const arr = list ?? [];
    return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
  }, []);

  const template = filter.templateSystemName ? templateByName.get(filter.templateSystemName) : undefined;
  const { healthOptions, lifecycleOptions, stageOptions } = buildFilterArcOptions({ t, template });
  const stageEnabled = !!filter.templateSystemName;
  const hasPredicate =
    !!filter.stageId?.length || !!filter.healthStatus?.length || !!filter.lifecycle?.length;
  const showMinCount = filter.operator === 'in';
  // Undefined `axes` renders every axis, matching every caller before this prop existed.
  const showAxis = (axis: 'health' | 'stage' | 'lifecycle') => !axes || axes.includes(axis);
  const selectedSelectorOption = selectorOptions.find((o) => o.value === toSelectorValue(filter));

  return (
    <div
      className={`arc-value-editor-molecule ${className}`.trim()}
      data-testid={`arc-value-editor-${index}`}
      data-container={container}
    >
      <div className="arc-value-editor-molecule__row-main">
        {lockArcSelector ? (
          <span
            className="arc-value-editor-molecule__selector-value"
            data-testid={`arc-selector-locked-label-${index}`}
          >
            {selectedSelectorOption && (
              <span className="arc-value-editor-molecule__selector-value-glyph" aria-hidden="true">
                <GlyphAtom glyph={selectedSelectorOption.glyph} />
              </span>
            )}
            <span>{selectedSelectorOption?.label}</span>
          </span>
        ) : (
          <DropdownAtom
            value={toSelectorValue(filter)}
            options={selectorOptions}
            itemTemplate={selectorItemTemplate}
            valueTemplate={selectorValueTemplate}
            pt={selectorOptionPassThrough}
            focusInputAttrs={selectorFocusInputAttrs}
            // The prototype's ACSelectorMenu lists every selector (any, primary, each
            // template) without scrolling; PrimeReact's 200px default clipped the
            // fifth row. Tall enough for the templates an org realistically has,
            // still bounded so a runaway list stays usable.
            scrollHeight="24rem"
            onChange={(e) => !readOnly && changeSelector(e.value ?? 'any')}
            disabled={readOnly}
            loading={templatesLoading}
            className="arc-value-editor-molecule__selector"
            data-testid={`arc-selector-${index}`}
            ariaLabel={t('context.editor.arcFilters.selectorLabel')}
          />
        )}
        {templatesFailed && (
          <span
            className="arc-value-editor-molecule__templates-error"
            role="status"
            data-testid={`arc-templates-error-${index}`}
          >
            <GlyphAtom glyph="lucide:triangle-alert" />
            {t('context.editor.arcFilters.templatesLoadFailed')}
          </span>
        )}
        {/* uf-arc.tabs (EAINT-908 Phase 2) — the prototype's is/is-not control is a tab
            strip; this wrapper carries the datum id without touching
            SegmentedControlAtom, which the spec's Reuse Audit lists unchanged. */}
        <div data-datum={dtm(tabsDatumId, 'uf-arc.tabs')} data-kind="tabs">
          <SegmentedControlAtom
            options={[
              { value: 'in', label: t('context.editor.arcFilters.is') },
              { value: 'not_in', label: t('context.editor.arcFilters.isNot') },
            ]}
            value={filter.operator}
            onChange={(v) => !readOnly && onChange({ ...filter, operator: v as 'in' | 'not_in' })}
            mode="single"
            size="small"
            ariaLabel={t('context.editor.arcFilters.is')}
          />
        </div>
        {!readOnly && (
          <button
            type="button"
            className="arc-value-editor-molecule__advanced-toggle"
            onClick={toggleExpanded}
            data-testid={`arc-expand-${index}`}
            aria-expanded={expanded}
            aria-controls={expanded ? advancedRegionId : undefined}
            aria-label={t('context.editor.arcFilters.editArcCondition')}
            // uf-arc.edit-arc-condition (EAINT-908 Phase 2) — same affordance and copy as
            // the prototype's "Edit arc condition" icon button (arc-filter.jsx:387).
            data-datum="uf-arc.edit-arc-condition"
            data-kind="button"
          >
            <i className="pi pi-sliders-h" aria-hidden="true" />
          </button>
        )}
        {!readOnly && onRemove && (
          <button
            type="button"
            className="arc-value-editor-molecule__remove-btn"
            aria-label={t('context.editor.arcFilters.removeFilter')}
            data-testid={`arc-remove-${index}`}
            onClick={onRemove}
          >
            <i className="pi pi-times" aria-hidden="true" />
          </button>
        )}
      </div>

      {/* uf-arc.predicate-heading (EAINT-908 Phase 4) — arc-filter.jsx:290; the
          negated form reads "no arc may match" rather than "the same arc must
          match" since a not_in row asserts absence, not correlation. */}
      <div className="arc-value-editor-molecule__predicate-heading" data-testid={`arc-predicate-heading-${index}`}>
        {filter.operator === 'not_in'
          ? t('context.editor.arcFilters.predicateHeadingNotIn')
          : t('context.editor.arcFilters.predicateHeadingIn')}
      </div>
      {/* One bordered region = one arc. Every predicate inside is ANDed and
          correlated on the SAME arc — two different arcs need two rows
          (arc-filter-ui-brief.md §2). */}
      <div className="arc-value-editor-molecule__predicate">
        {showAxis('health') && (
        <div className="arc-value-editor-molecule__axis">
          <span
            className="arc-value-editor-molecule__axis-label"
            data-datum={dtm(healthLabelDatumId, 'uf-arc.health')}
            data-kind="label"
          >
            {t('context.editor.arcFilters.health')}
          </span>
          {/* uf-arc.chip#2 (EAINT-908 Phase 3) — the predicate grid's Health toggle chips
              (arc-filter.jsx:298, AC_HEALTH each()). The prototype renders these
              always-visible (ACHealthChip has no dropdown, no open/closed state), so
              the axis is a `role="group"` of toggle buttons rather than an overlay
              control forced open — a real UI change, not a display wrapper around
              `MultiSelectAtom`, whose closed-by-default single-panel overlay cannot
              hold three simultaneously-open axes for one capture. */}
          <fieldset
            className="arc-value-editor-molecule__chip-group arc-value-editor-molecule__chip-group--health"
            disabled={readOnly}
            data-container="each(AC_HEALTH)"
            data-testid={`arc-health-${index}`}
          >
            <legend className="arc-value-editor-molecule__visually-hidden">
              {t('context.editor.arcFilters.health')}
            </legend>
            {healthOptions.map((option) => {
              const selected = (filter.healthStatus ?? []).includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  className={[
                    'arc-value-editor-molecule__chip-option',
                    `arc-value-editor-molecule__health-option--${getArcHealthModifier(option.value)}`,
                    selected ? 'arc-value-editor-molecule__chip-option--selected' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  aria-pressed={selected}
                  disabled={readOnly}
                  data-testid={`arc-health-${index}-${option.value}`}
                  data-datum={dtm(healthChipDatumId, 'uf-arc.chip#2')}
                  data-kind="chip"
                  onClick={() => updateAxis('healthStatus', toggleInAxis(filter.healthStatus, option.value))}
                >
                  {option.label}
                </button>
              );
            })}
          </fieldset>
        </div>
        )}

        {showAxis('stage') && (
        <div className="arc-value-editor-molecule__axis">
          <span
            className="arc-value-editor-molecule__axis-label"
            data-datum={dtm(stageLabelDatumId, 'uf-arc.stage')}
            data-kind="label"
          >
            {t('context.editor.arcFilters.stage')}
          </span>
          {/* uf-arc.chip#3 (EAINT-908 Phase 3) — Stage toggle chips (arc-filter.jsx:309,
              tmpl.stages each()); empty until a template narrows the stage list. A
              template that DOES narrow it but declares no stages of its own gets
              EmptyStateAtom instead of a fieldset with nothing in it — see
              templateWithoutStagesShowsEmptyState. */}
          {stageEnabled && stageOptions.length === 0 ? (
            <div data-testid={`arc-stage-empty-${index}`}>
              <EmptyStateAtom
                icon="pi-sitemap"
                title={t('context.editor.arcFilters.templateHasNoStages')}
                className="arc-value-editor-molecule__stage-empty"
              />
            </div>
          ) : (
            <fieldset
              className="arc-value-editor-molecule__chip-group"
              disabled={readOnly || !stageEnabled}
              data-container="each(tmpl.stages)"
              data-testid={`arc-stage-${index}`}
            >
              <legend className="arc-value-editor-molecule__visually-hidden">
                {t('context.editor.arcFilters.stage')}
              </legend>
              {stageOptions.map((option) => {
                const selected = (filter.stageId ?? []).includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    className={[
                      'arc-value-editor-molecule__chip-option',
                      selected ? 'arc-value-editor-molecule__chip-option--selected' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    aria-pressed={selected}
                    disabled={readOnly || !stageEnabled}
                    data-testid={`arc-stage-${index}-${option.value}`}
                    data-datum={dtm(stageChipDatumId, 'uf-arc.chip#3')}
                    data-kind="chip"
                    onClick={() => updateAxis('stageId', toggleInAxis(filter.stageId, option.value))}
                  >
                    {option.label}
                  </button>
                );
              })}
            </fieldset>
          )}
          {!stageEnabled && (
            <span className="arc-value-editor-molecule__hint" data-testid={`arc-stage-hint-${index}`}>
              {t('context.editor.arcFilters.stageNeedsTemplate')}
            </span>
          )}
          {/* uf-arc.hint#2 (EAINT-908 Phase 4) — the dashed lock box that replaces the
              (empty) stage chip row while no template narrows it (arc-filter.jsx:311-314).
              When the main selector is locked (a `focusArc` column, e.g. Atlas's Stage
              column) and more than one template is in play, the lock box becomes a
              secondary "narrow by template" picker (EAINT-936 OQ-11) — the locked arc
              itself (`filter.arcSelector`) is never touched, only `templateSystemName`,
              which exists solely to narrow which template's stage vocabulary this axis
              offers. With one or zero templates the plain lock hint remains: one template
              is auto-selected upstream (`onlyTemplate`), and zero leaves nothing to
              narrow by. */}
          {!stageEnabled && lockArcSelector && arcTemplates.length > 1 ? (
            <div className="arc-value-editor-molecule__stage-locked" data-testid={`arc-stage-locked-${index}`}>
              <label htmlFor={narrowTemplateSelectId} className="arc-value-editor-molecule__visually-hidden">
                {t('columnFilters.stage.narrowByTemplate')}
              </label>
              <DropdownAtom
                inputId={narrowTemplateSelectId}
                value={undefined}
                options={arcTemplates.map((tpl) => ({ label: tpl.name, value: tpl.systemName }))}
                placeholder={t('columnFilters.stage.pickTemplate')}
                onChange={(e) => !readOnly && onChange({ ...filter, templateSystemName: e.value })}
                disabled={readOnly}
                className="arc-value-editor-molecule__stage-narrow-select"
                data-testid={`arc-stage-template-narrow-${index}`}
              />
            </div>
          ) : (
            !stageEnabled && (
              <div className="arc-value-editor-molecule__stage-locked" data-testid={`arc-stage-locked-${index}`}>
                <i className="pi pi-lock" aria-hidden="true" />
                {t('columnFilters.stage.pickTemplate')}
              </div>
            )
          )}
        </div>
        )}

        {showAxis('lifecycle') && (
        <div className="arc-value-editor-molecule__axis">
          <span
            className="arc-value-editor-molecule__axis-label"
            data-datum={dtm(lifecycleLabelDatumId, 'uf-arc.lifecycle')}
            data-kind="label"
          >
            {t('context.editor.arcFilters.lifecycle')}
          </span>
          {/* uf-arc.chip#4 (EAINT-908 Phase 3) — Lifecycle toggle chips (arc-filter.jsx:321,
              AC_LIFECYCLE each()). */}
          <fieldset
            className="arc-value-editor-molecule__chip-group arc-value-editor-molecule__chip-group--lifecycle"
            disabled={readOnly}
            data-container="each(AC_LIFECYCLE)"
            data-testid={`arc-lifecycle-${index}`}
          >
            <legend className="arc-value-editor-molecule__visually-hidden">
              {t('context.editor.arcFilters.lifecycle')}
            </legend>
            {lifecycleOptions.map((option) => {
              const selected = (filter.lifecycle ?? []).includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  className={[
                    'arc-value-editor-molecule__chip-option',
                    selected ? 'arc-value-editor-molecule__chip-option--selected' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  aria-pressed={selected}
                  disabled={readOnly}
                  data-testid={`arc-lifecycle-${index}-${option.value}`}
                  data-datum={dtm(lifecycleChipDatumId, 'uf-arc.chip#4')}
                  data-kind="chip"
                  onClick={() => updateAxis('lifecycle', toggleInAxis(filter.lifecycle, option.value))}
                >
                  {option.label}
                </button>
              );
            })}
          </fieldset>
        </div>
        )}

        {!hasPredicate && (
          <span className="arc-value-editor-molecule__existence" data-testid={`arc-existence-hint-${index}`}>
            {/* An empty predicate is a legitimate existence check
                ("has an onboarding arc"), not an unfinished row (brief §6). */}
            {t('context.editor.arcFilters.existenceCheck')}
          </span>
        )}
      </div>

      {expanded && showMinCount && (
        <div id={advancedRegionId} className="arc-value-editor-molecule__mincount" data-testid={`arc-mincount-${index}`}>
          <span
            className="arc-value-editor-molecule__mincount-label"
            data-datum={dtm(atLeastLabelDatumId, 'uf-arc.at-least')}
            data-kind="label"
          >
            {t('context.editor.arcFilters.atLeast')}
          </span>
          <div className="arc-value-editor-molecule__mincount-stepper">
            <ButtonAtom
              type="button"
              icon="pi pi-minus"
              variant="ghost"
              shape="icon-square"
              size="small"
              onClick={() => onChange({ ...filter, minCount: Math.max(1, (filter.minCount ?? 1) - 1) })}
              disabled={readOnly}
              aria-label={t('context.editor.arcFilters.decreaseMinCount')}
              data-datum={dtm(minCountDecreaseDatumId, 'uf-arc.button#5')}
              data-kind="button"
            />
            <span className="arc-value-editor-molecule__mincount-value">{filter.minCount ?? 1}</span>
            <ButtonAtom
              type="button"
              icon="pi pi-plus"
              variant="ghost"
              shape="icon-square"
              size="small"
              onClick={() => onChange({ ...filter, minCount: (filter.minCount ?? 1) + 1 })}
              disabled={readOnly}
              aria-label={t('context.editor.arcFilters.increaseMinCount')}
              data-datum={dtm(minCountIncreaseDatumId, 'uf-arc.button#6')}
              data-kind="button"
            />
          </div>
          <span className="arc-value-editor-molecule__mincount-label">
            {t('context.editor.arcFilters.atLeastSuffix')}
          </span>
        </div>
      )}
      {expanded && !showMinCount && (
        // uf-arc.min-count-na (EAINT-908 Phase 4) — arc-filter.jsx:337-339; the "is not"
        // counterpart to the "at least" stepper above — a negated row asserts no arc
        // matches at all, so a minimum count has nothing to count.
        <div id={advancedRegionId} className="arc-value-editor-molecule__mincount-na" data-testid={`arc-mincount-na-${index}`}>
          <i className="pi pi-info-circle" aria-hidden="true" />
          {t('context.editor.arcFilters.minCountNotApplicable')}
        </div>
      )}
    </div>
  );
};

export default ArcValueEditorMolecule;
